import { JsonObject, JsonProperty, JsonType } from "ta-json";

import type { Nullable } from "../../base-types";
import { CultureLoadOption, ICultureLoadOption } from "./culture-load-option";
import { IPropertyLoadOption, PropertyLoadOption } from "./property-load-option";
import { IRelationLoadOption, RelationLoadOption } from "./relation-load-option";

/**
 * Represents a loading configuration for querying ids or entities.
 */
export interface IQueryLoadConfiguration {
    /**
     * Gets a value specifying whether entities or ids need to be returned.
     * When false, no entities will be loaded and the load options will be ignored.
     */
    loadEntities: boolean;

    /**
     * Gets the culture load option.
     */
    cultureLoadOption: Nullable<ICultureLoadOption>;

    /**
     * Gets the property load option.
     */
    propertyLoadOption: Nullable<IPropertyLoadOption>;

    /**
     * Gets the relation load option.
     */
    relationLoadOption: Nullable<IRelationLoadOption>;
}

@JsonObject()
export class QueryLoadConfiguration {
    @JsonProperty("load_entities")
    loadEntities: boolean = false;

    @JsonProperty("culture_option")
    @JsonType(CultureLoadOption)
    cultureLoadOption: Nullable<ICultureLoadOption>;

    @JsonProperty("property_option")
    @JsonType(PropertyLoadOption)
    propertyLoadOption: Nullable<IPropertyLoadOption>;

    @JsonProperty("relation_option")
    @JsonType(RelationLoadOption)
    relationLoadOption: Nullable<IRelationLoadOption>;

    //#region Configurations
    static Ids: Readonly<IQueryLoadConfiguration> = new QueryLoadConfiguration({
        cultureLoadOption: CultureLoadOption.None,
        propertyLoadOption: PropertyLoadOption.None,
        relationLoadOption: RelationLoadOption.None,
        loadEntities: false,
    });

    static Minimal: Readonly<IQueryLoadConfiguration> = new QueryLoadConfiguration({
        cultureLoadOption: CultureLoadOption.None,
        propertyLoadOption: PropertyLoadOption.None,
        relationLoadOption: RelationLoadOption.None,
    });

    static Default: Readonly<IQueryLoadConfiguration> = new QueryLoadConfiguration({
        cultureLoadOption: CultureLoadOption.Default,
        propertyLoadOption: PropertyLoadOption.All,
        relationLoadOption: RelationLoadOption.None,
    });

    static DefaultCultureFull: Readonly<IQueryLoadConfiguration> = new QueryLoadConfiguration({
        cultureLoadOption: CultureLoadOption.Default,
        propertyLoadOption: PropertyLoadOption.All,
        relationLoadOption: RelationLoadOption.All,
    });

    static Full: Readonly<IQueryLoadConfiguration> = new QueryLoadConfiguration({
        cultureLoadOption: CultureLoadOption.All,
        propertyLoadOption: PropertyLoadOption.All,
        relationLoadOption: RelationLoadOption.All,
    });
    //#endregion

    constructor(init?: Partial<IQueryLoadConfiguration>) {
        Object.assign(this, init);

        if (init != null && init.loadEntities == null) {
            this.loadEntities = true;
        }
    }

    deepClone(): IQueryLoadConfiguration {
        const result = new QueryLoadConfiguration();

        result.loadEntities = this.loadEntities;
        result.cultureLoadOption = this.cultureLoadOption && this.cultureLoadOption.deepClone();
        result.propertyLoadOption = this.propertyLoadOption && this.propertyLoadOption.deepClone();
        result.relationLoadOption = this.relationLoadOption && this.relationLoadOption.deepClone();

        return result;
    }
}
